import React, { useState, useEffect, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { initializeApp, getApps } from 'firebase/app';
import { buildApiUrl } from '../config/api';
import './VideoAnalysisPage.css';

// Firebase config (should match your project)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'futeval-plus',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'futeval-plus.appspot.com',
};

// Initialize Firebase (only if not already initialized)
let app;
const apps = getApps();
if (apps.length === 0) {
  app = initializeApp(firebaseConfig);
} else {
  app = apps[0];
}

const firestore = getFirestore(app);
const storage = getStorage(app);
const functions = getFunctions(app);

interface Player {
  player_id: number;
  first_name: string;
  last_name: string;
  full_name?: string;
}

interface VideoAnalysisPageProps {
  onBack: () => void;
}

const VideoAnalysisPage: React.FC<VideoAnalysisPageProps> = ({ onBack }) => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>('');
  const [cameraAngle, setCameraAngle] = useState<string>('SIDE');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string>('');
  const [jobProgress, setJobProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const playersLoadedRef = useRef<boolean>(false); // Guard to prevent infinite loops

  // Load players - using the same endpoint as "View all my players"
  useEffect(() => {
    // Guard: only load once
    if (playersLoadedRef.current) {
      return;
    }

    const loadPlayers = async () => {
      // Set guard immediately to prevent concurrent calls
      playersLoadedRef.current = true;

      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          setError('Authentication required');
          setLoading(false);
          return;
        }

        // Use the same endpoint as CoachDashboard: /api/coaches/my-players
        const response = await fetch(buildApiUrl('/coaches/my-players'), {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setPlayers(data.players || []);
          setError(null); // Clear any previous errors
        } else {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.detail || errorData.error || 'Failed to load players';
          setError(errorMessage);
          console.error('Failed to load players:', response.status, errorData);
        }
      } catch (err: any) {
        console.error('Error loading players:', err);
        setError('Failed to load players: ' + (err.message || 'Network error'));
        // Reset guard on error so user can retry (but not auto-retry)
        playersLoadedRef.current = false;
      } finally {
        setLoading(false);
      }
    };

    loadPlayers();
  }, []); // Empty deps: only run once on mount

  // Listen to Firestore for job updates
  useEffect(() => {
    if (!jobId) return;

    const jobRef = doc(firestore, 'analysisJobs', jobId);
    const unsubscribe = onSnapshot(jobRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setJobStatus(data.status || '');
        setJobProgress(data.progress || 0);

        if (data.status === 'DONE') {
          setResults({
            metrics: data.metrics || {},
            faults: data.faults || [],
            reportText: data.reportText || '',
            outputs: data.outputs || {},
          });
        } else if (data.status === 'FAILED') {
          setError(data.error || 'Analysis failed');
        }
      }
    });

    return () => unsubscribe();
  }, [jobId]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('video/')) {
        setError('Please select a video file (MP4 or MOV)');
        return;
      }
      // Validate file size (60MB max)
      if (file.size > 60 * 1024 * 1024) {
        setError('File size must be less than 60MB');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleStartAnalysis = async () => {
    if (!selectedFile) {
      setError('Please select a video file');
      return;
    }
    if (!selectedPlayerId) {
      setError('Please select a player');
      return;
    }

    setError(null);
    setIsUploading(true);
    setUploadProgress(0);
    setResults(null);

    try {
      // Call createAnalysisJob Cloud Function
      const createJob = httpsCallable(functions, 'createAnalysisJob');
      const result = await createJob({
        playerId: selectedPlayerId,
        cameraAngle,
      });

      const data = result.data as any;
      if (!data.success || !data.jobId || !data.uploadUrl) {
        throw new Error(data.error || 'Failed to create analysis job');
      }

      const newJobId = data.jobId;
      const uploadUrl = data.uploadUrl;
      setJobId(newJobId);
      setJobStatus('QUEUED');

      // Upload video to signed URL
      await uploadVideoToSignedUrl(selectedFile, uploadUrl);

      // Upload complete - job will be processed by onAnalysisUploadFinalized trigger
      setJobStatus('RUNNING');
    } catch (err: any) {
      console.error('Error starting analysis:', err);
      setError(err.message || 'Failed to start analysis');
      setIsUploading(false);
    }
  };

  const uploadVideoToSignedUrl = (file: File, signedUrl: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setIsUploading(false);
          resolve();
        } else {
          setIsUploading(false);
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        setIsUploading(false);
        reject(new Error('Upload failed'));
      });

      xhr.addEventListener('abort', () => {
        setIsUploading(false);
        reject(new Error('Upload aborted'));
      });

      xhr.open('PUT', signedUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  };

  const handleDownloadArtifact = async (path: string, filename: string) => {
    try {
      const storageRef = ref(storage, path);
      const url = await getDownloadURL(storageRef);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading artifact:', err);
      setError('Failed to download artifact');
    }
  };

  return (
    <div className="video-analysis-page">
      <div className="video-analysis-container">
        <div className="video-analysis-header">
          <h2>Video Analysis - Shooting Mechanics</h2>
          <button onClick={onBack} className="back-button">
            ← Back
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {!results && (
          <div className="upload-section">
            <div className="form-group">
              <label>Select Player</label>
              <select
                value={selectedPlayerId}
                onChange={(e) => setSelectedPlayerId(e.target.value)}
                disabled={loading || isUploading}
              >
                <option value="">Select a player...</option>
                {players.map((player) => (
                  <option key={player.player_id} value={player.player_id}>
                    {player.full_name || `${player.first_name} ${player.last_name}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Camera Angle</label>
              <select
                value={cameraAngle}
                onChange={(e) => setCameraAngle(e.target.value)}
                disabled={isUploading}
              >
                <option value="SIDE">SIDE</option>
                <option value="FRONT_45">FRONT_45</option>
                <option value="FRONT">FRONT</option>
                <option value="REAR_45">REAR_45</option>
                <option value="REAR">REAR</option>
              </select>
            </div>

            <div className="form-group">
              <label>Select Video File</label>
              <input
                type="file"
                accept="video/mp4,video/mov"
                onChange={handleFileSelect}
                disabled={isUploading}
              />
              <small>MP4 or MOV, max 60MB, 5-15 seconds recommended</small>
            </div>

            <button
              onClick={handleStartAnalysis}
              disabled={!selectedFile || !selectedPlayerId || isUploading}
              className="start-analysis-button"
            >
              {isUploading ? 'Uploading...' : 'Start Analysis'}
            </button>

            {isUploading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <div className="progress-text">{Math.round(uploadProgress)}%</div>
              </div>
            )}

            {jobId && jobStatus && (
              <div className="job-status">
                <div className="status-label">Status: {jobStatus}</div>
                {jobProgress > 0 && (
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${jobProgress}%` }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {results && (
          <div className="results-section">
            <h3>Analysis Results</h3>

            {results.metrics && Object.keys(results.metrics).length > 0 && (
              <div className="metrics-section">
                <h4>Metrics</h4>
                <table className="metrics-table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(results.metrics).map(([key, value]) => (
                      <tr key={key}>
                        <td>{key.replace(/_/g, ' ')}</td>
                        <td>{String(value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {results.faults && results.faults.length > 0 && (
              <div className="faults-section">
                <h4>Priority Faults</h4>
                {results.faults.map((fault: any, index: number) => (
                  <div key={index} className="fault-item">
                    <h5>{fault.title}</h5>
                    <p><strong>Why:</strong> {fault.why}</p>
                    <p><strong>Cue:</strong> {fault.cue}</p>
                    {fault.drills && fault.drills.length > 0 && (
                      <div>
                        <strong>Drills:</strong>
                        <ul>
                          {fault.drills.map((drill: string, i: number) => (
                            <li key={i}>{drill}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {results.reportText && (
              <div className="report-section">
                <h4>Report</h4>
                <pre className="report-text">{results.reportText}</pre>
              </div>
            )}

            {results.outputs && (
              <div className="artifacts-section">
                <h4>Download Artifacts</h4>
                <div className="artifact-buttons">
                  {results.outputs.poseJsonPath && (
                    <button
                      onClick={() => handleDownloadArtifact(results.outputs.poseJsonPath, 'pose.json')}
                      className="download-button"
                    >
                      Download Pose Data
                    </button>
                  )}
                  {results.outputs.metricsJsonPath && (
                    <button
                      onClick={() => handleDownloadArtifact(results.outputs.metricsJsonPath, 'metrics.json')}
                      className="download-button"
                    >
                      Download Metrics
                    </button>
                  )}
                  {results.outputs.reportTextPath && (
                    <button
                      onClick={() => handleDownloadArtifact(results.outputs.reportTextPath, 'report.txt')}
                      className="download-button"
                    >
                      Download Report
                    </button>
                  )}
                </div>
              </div>
            )}

            <button onClick={() => {
              setResults(null);
              setJobId(null);
              setJobStatus('');
              setSelectedFile(null);
            }} className="new-analysis-button">
              New Analysis
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoAnalysisPage;
